import React, { ReactElement } from 'react';
import EventVolumeByDay from './components/EventVolumeByDay';
import EventClosureStatusByWeek from './components/EventClosureStatusByWeek';
import DetectionCasesByAlertType from './components/DetectionCasesByAlertType';
import DetectionCaseStatisticsTable from './components/DetectionCaseStatisticsTable';
import DetectionCaseVolumeByDay from './components/DetectionCaseVolumeByDay';
import EventStatistics from './components/EventStatistics';
import DetectionCasesByPriority from './components/DetectionCasesByPriority';
import EventsByAlertType from './components/EventsByAlertType';
import EventsByTactic from './components/EventsByTactic';
import EventsByTechnique from './components/EventsByTechnique';
import DetectionCaseByClosureReason from './components/DetectionCaseByClosureReason';
import EventClosureStatus from './components/EventClosureStatus';
import EventVolumeByWeek from './components/EventVolumeByWeek';
import MdrCases from './components/MdrCases';

type StatusReportParams = {
  clientUid: string;
  reportStart: string;
  reportEnd: string;
  securityEventApi: string;
};

export default function StatusReport(props: StatusReportParams): ReactElement {
  const { clientUid, reportStart, reportEnd, securityEventApi } = props;

  return (
    <>
      <div className="report-section page-break" id="event-statistics-report-section">
        <EventStatistics clientUid={clientUid} reportStart={reportStart} reportEnd={reportEnd} />
      </div>

      <div className="report-section" id="statusReportSection1">
        <EventsByAlertType {...props} />
      </div>

      <div className="report-section" id="statusReportSection2">
        <EventsByTactic {...props} />
      </div>
      <div className="report-section" id="statusReportSection3">
        <EventsByTechnique {...props} />
      </div>
      <div className="report-section" id="statusReportSection4">
        <EventClosureStatus {...props} />
      </div>

      <div className="report-section" id="statusReportSection5">
        <EventVolumeByDay clientUid={clientUid} reportStart={reportStart} reportEnd={reportEnd} />
      </div>

      <div className="report-section" id="statusReportSection6">
        <EventVolumeByWeek clientUid={clientUid} reportStart={reportStart} reportEnd={reportEnd} />
      </div>
      <div className="report-section" id="statusReportSection7">
        <EventClosureStatusByWeek
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      </div>

      <div className="report-section page-break">
        <DetectionCaseStatisticsTable
          securityEventApi={securityEventApi}
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      </div>

      <div className="report-section">
        <DetectionCaseVolumeByDay
          securityEventApi={securityEventApi}
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      </div>
      <div className="report-section">
        <DetectionCasesByPriority
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      </div>
      <div className="report-section">
        <DetectionCaseByClosureReason
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      </div>
      <div className="report-section">
        <DetectionCasesByAlertType
          clientUid={clientUid}
          reportStart={reportStart}
          reportEnd={reportEnd}
        />
      </div>

      <div className="report-section page-break">
        <MdrCases {...props} showClosed={false} />
      </div>

      <div className="report-section page-break">
        <h2>Appendix A</h2>
        <h3>MITRE ATT&CK Framework</h3>
        <p>
          The MITRE ATT&CK Framework is a way of understanding how attackers move through a system.
          It's like a playbook for attackers, and it can be used by defenders to predict and stop
          attacks.
        </p>
        <p>
          The framework is divided into two parts: tactics and techniques. Tactics are the
          high-level goals that attackers want to achieve, like gaining access to a system or
          stealing data. Techniques are the specific steps that attackers take to achieve their
          tactics.
        </p>
        <p>
          For a more in-depth look at the Framework, visit their website at{' '}
          <a href="https://attack.mitre.org" target="_blank" rel="noreferrer noopener">
            https://attack.mitre.org
          </a>
          .
        </p>
        <h3>Event/Case Closure Statuses</h3>

        <ul>
          <li>
            <strong>Known Issue: </strong>Known issues are events generated by known system
            processes which, by design, behave in a suspicious manner and which cannot be tuned
            further, but are allowed by the client. Examples include: Vulnerability Scanners, Avaya
            VPNs, expired Kerberos tickets from Outlook, etc.
          </li>
          <li>
            <strong>Accepted Behavior: </strong>User behavior which, while suspicious, the client
            has indicated is permissible, expected, or cannot be stopped. Examples include VPN
            Ingress from foreign country where user is known to be travelling to, permitted user
            creation activity by admins, other behavior which has been documented to be permitted.
          </li>
          <li>
            <strong>Tuning Required: </strong>System behavior generating alerts which are erroneous
            or otherwise valueless, and which requires additional tuning or reconfiguration to
            reduce noise. Examples: Improperly deployed networks sensors, improperly correlated
            rules, erroneous threat intelligence feeds.
          </li>
          <li>
            <strong>No Action Needed: </strong>Customer-submitted events, SPAM, test events, and
            garbled messages which require no action, or for which work has already been completed,
            and which do not require a response, or a response has already been sent by other means.
          </li>
          <li>
            <strong>Resolved: </strong>Incident has been resolved and closed. Any work instructions
            and / or tuning has been completed.
          </li>
          <li>
            <strong>No Response: </strong>Incident was escalated to the client, and DirectDefense
            did not receive a response.
          </li>
          <li>
            <strong>Linked: </strong>Event was added to an existing case.
          </li>
        </ul>
      </div>
    </>
  );
}
